/**
 * TrainAITable - inline editable Train AI table.
 *
 * Replaces the modal-based "Add Training Rule" flow with a single table where
 * the user can add as many rules as they want in one go and save them
 * one-by-one or batch-save the entire draft set.
 *
 * Three user-led design decisions baked in (Feb 2026):
 *   1) Amount Range is no longer 3 hardcoded buckets - user types a min and max
 *      directly, prefixed with the business's base-currency symbol.
 *   2) Inline draft rows can be added repeatedly without re-opening any modal.
 *   3) Existing read-only training examples render in the same table for
 *      visual consistency.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Plus, Save, Trash2, Loader2, ArrowUpRight, ArrowDownRight, Check, X, Brain,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// iter 739 P0-B - Humanise a snake_case backend classification token.
const humanise = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Fallback lists kept in case the schema-info endpoint is unreachable
// (e.g. offline preview). These match the legacy hardcoded set.
const FALLBACK_OUTFLOW = [
  'bill_payment', 'other_payment', 'loan_repayment', 'owner_withdrawal',
  'bank_charges', 'tax_payment', 'salary_payment',
];
const FALLBACK_INFLOW = [
  'invoice_payment', 'other_receipt', 'loan_received', 'owner_contribution',
  'interest_received', 'refund_received',
];
const FALLBACK_TREATMENTS = ['expense', 'prepayment', 'fixed_asset', 'inventory', 'settlement'];

const CURRENCY_SYMBOL = {
  NGN: '₦', USD: '$', GBP: '£', EUR: '€', KES: 'KSh', GHS: 'GH₵', ZAR: 'R', INR: '₹',
};

const blankDraft = () => ({
  _key: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  direction: 'outflow',
  counterparty: '',
  description: '',
  amount_min: '',
  amount_max: '',
  classification: '',
  gl_code: '',
  gl_name: '',
  treatment: 'expense',
  notes: '',
  _saving: false,
});

const TrainAITable = ({
  existingExamples = [],
  glAccounts = [],
  baseCurrency = 'NGN',
  loading = false,
  onAfterSave,
  onToggleActive,
  onDelete,
}) => {
  const [drafts, setDrafts] = useState([]);
  const [batchSaving, setBatchSaving] = useState(false);
  // iter 739 P0-B - dynamic schema vocabulary from backend
  const [schema, setSchema] = useState({
    inflow: FALLBACK_INFLOW,
    outflow: FALLBACK_OUTFLOW,
    treatments: FALLBACK_TREATMENTS,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/ai-corrections/learning/schema-info`);
        if (cancelled || !resp.data) return;
        setSchema({
          inflow: resp.data.inflow_classifications || FALLBACK_INFLOW,
          outflow: resp.data.outflow_classifications || FALLBACK_OUTFLOW,
          treatments: resp.data.treatments || FALLBACK_TREATMENTS,
        });
      } catch (e) {
        // Silent: use fallbacks. UI still works.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const OUTFLOW_CLASSIFICATIONS = useMemo(
    () => schema.outflow.map((v) => ({ value: v, label: humanise(v) })),
    [schema.outflow],
  );
  const INFLOW_CLASSIFICATIONS = useMemo(
    () => schema.inflow.map((v) => ({ value: v, label: humanise(v) })),
    [schema.inflow],
  );
  const TREATMENT_OPTIONS = useMemo(
    () => schema.treatments.map((v) => ({ value: v, label: humanise(v) })),
    [schema.treatments],
  );

  const currencySymbol = CURRENCY_SYMBOL[baseCurrency] || baseCurrency;

  const addDraft = () => setDrafts(d => [...d, blankDraft()]);
  const updateDraft = (key, field, value) => {
    setDrafts(d => d.map(row => (row._key === key ? { ...row, [field]: value } : row)));
  };
  const removeDraft = (key) => setDrafts(d => d.filter(row => row._key !== key));

  const validateDraft = (row) => {
    if (!row.classification) return 'Classification is required';
    if (!row.counterparty && !row.description) return 'Either counterparty or description is required';
    const min = row.amount_min === '' ? null : Number(row.amount_min);
    const max = row.amount_max === '' ? null : Number(row.amount_max);
    if (min != null && max != null && min > max) return 'Min amount must be ≤ Max amount';
    return null;
  };

  const buildPayload = (row) => {
    const min = row.amount_min === '' ? null : Number(row.amount_min);
    const max = row.amount_max === '' ? null : Number(row.amount_max);
    const account = glAccounts.find(a => a.code === row.gl_code);
    const payload = {
      description: row.description || '',
      counterparty: row.counterparty || '',
      direction: row.direction,
      classification: row.classification,
      amount_min: min,
      amount_max: max,
      // Derived legacy bucket so the backend's amount_range index stays useful.
      amount_range: (min == null && max == null) ? 'any' : 'custom',
      notes: row.notes || '',
      // iter 739 P0-B - capture treatment so settlements / prepayments
      // / fixed assets get the right downstream posting behaviour.
      treatment: row.treatment || 'expense',
    };
    // Only include GL fields when non-empty - backend rejects '' as
    // "GL account does not exist". The GL select is labeled Optional.
    if (row.gl_code) {
      payload.gl_code = row.gl_code;
      payload.gl_name = account?.name || row.gl_name || '';
    }
    return payload;
  };

  const saveOne = async (row) => {
    const err = validateDraft(row);
    if (err) { toast.error(err); return false; }
    updateDraft(row._key, '_saving', true);
    try {
      const resp = await axios.post(`${API}/ai-corrections/training-examples`, buildPayload(row));
      if (resp.data?.success) {
        toast.success('Training rule added');
        removeDraft(row._key);
        onAfterSave?.();
        return true;
      }
      toast.error(resp.data?.message || 'Could not save rule');
      return false;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save rule');
      return false;
    } finally {
      updateDraft(row._key, '_saving', false);
    }
  };

  const saveAll = async () => {
    if (drafts.length === 0) return;
    setBatchSaving(true);
    let ok = 0;
    for (const row of drafts) {
      const success = await saveOne(row);
      if (success) ok += 1;
    }
    setBatchSaving(false);
    if (ok > 0) toast.success(`${ok} rule${ok === 1 ? '' : 's'} saved`);
  };

  const classificationOptionsFor = (direction) =>
    direction === 'inflow' ? INFLOW_CLASSIFICATIONS : OUTFLOW_CLASSIFICATIONS;

  const headerRow = (
    <div
      className="grid items-center text-[11px] uppercase tracking-wider text-muted-foreground font-medium px-3 py-2 border-b bg-muted/30"
      style={{ gridTemplateColumns: '90px 1.4fr 1.6fr 1.4fr 1.2fr 1fr 90px' }}
    >
      <div>Direction</div>
      <div>Counterparty</div>
      <div>Description / keywords</div>
      <div>Amount range ({currencySymbol})</div>
      <div>Classification</div>
      <div>GL account</div>
      <div className="text-right">Actions</div>
    </div>
  );

  const renderDraftRow = (row) => {
    const classifications = classificationOptionsFor(row.direction);
    return (
      <motion.div
        key={row._key}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        className="grid items-start gap-2 px-3 py-3 border-b bg-purple-50/30"
        style={{ gridTemplateColumns: '90px 1.4fr 1.6fr 1.4fr 1.2fr 1fr 90px' }}
        data-testid={`train-ai-draft-row-${row._key}`}
      >
        <Select value={row.direction} onValueChange={(v) => { updateDraft(row._key, 'direction', v); updateDraft(row._key, 'classification', ''); }}>
          <SelectTrigger className="h-9 text-xs" data-testid={`draft-direction-${row._key}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="outflow"><span className="flex items-center gap-1.5"><ArrowUpRight className="w-3 h-3 text-red-600" />Out</span></SelectItem>
            <SelectItem value="inflow"><span className="flex items-center gap-1.5"><ArrowDownRight className="w-3 h-3 text-emerald-600" />In</span></SelectItem>
          </SelectContent>
        </Select>

        <Input
          value={row.counterparty}
          placeholder="e.g., MTN"
          className="h-9 text-sm"
          onChange={(e) => updateDraft(row._key, 'counterparty', e.target.value)}
          data-testid={`draft-counterparty-${row._key}`}
        />

        <Input
          value={row.description}
          placeholder="e.g., monthly data plan"
          className="h-9 text-sm"
          onChange={(e) => updateDraft(row._key, 'description', e.target.value)}
          data-testid={`draft-description-${row._key}`}
        />

        <div className="flex items-center gap-1.5">
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{currencySymbol}</span>
            <Input
              type="number" min="0" inputMode="decimal"
              value={row.amount_min}
              placeholder="min"
              className="h-9 text-sm pl-7"
              onChange={(e) => updateDraft(row._key, 'amount_min', e.target.value)}
              data-testid={`draft-amount-min-${row._key}`}
            />
          </div>
          <span className="text-muted-foreground text-xs">–</span>
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{currencySymbol}</span>
            <Input
              type="number" min="0" inputMode="decimal"
              value={row.amount_max}
              placeholder="max"
              className="h-9 text-sm pl-7"
              onChange={(e) => updateDraft(row._key, 'amount_max', e.target.value)}
              data-testid={`draft-amount-max-${row._key}`}
            />
          </div>
        </div>

        <Select value={row.classification} onValueChange={(v) => updateDraft(row._key, 'classification', v)}>
          <SelectTrigger className="h-9 text-xs" data-testid={`draft-classification-${row._key}`}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {classifications.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={row.gl_code} onValueChange={(v) => updateDraft(row._key, 'gl_code', v)}>
          <SelectTrigger className="h-9 text-xs" data-testid={`draft-gl-${row._key}`}>
            <SelectValue placeholder="Optional" />
          </SelectTrigger>
          <SelectContent>
            {glAccounts.map(acc => (
              <SelectItem key={acc.code} value={acc.code}>
                <span className="font-mono text-[10px] text-muted-foreground mr-1">{acc.code}</span>
                {acc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-end gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-emerald-700 hover:bg-emerald-50"
            onClick={() => saveOne(row)}
            disabled={row._saving}
            title="Save row"
            data-testid={`draft-save-${row._key}`}
          >
            {row._saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-muted-foreground hover:bg-muted"
            onClick={() => removeDraft(row._key)}
            disabled={row._saving}
            title="Discard row"
            data-testid={`draft-discard-${row._key}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </motion.div>
    );
  };

  const renderExistingRow = (ex, index) => {
    const dirIn = ex.direction === 'inflow';
    const minMaxLabel = (ex.amount_min != null || ex.amount_max != null)
      ? `${ex.amount_min != null ? `${currencySymbol}${Number(ex.amount_min).toLocaleString()}` : '-'} – ${ex.amount_max != null ? `${currencySymbol}${Number(ex.amount_max).toLocaleString()}` : '-'}`
      : (ex.amount_range && ex.amount_range !== 'any' ? ex.amount_range : 'Any');
    return (
      <div
        key={ex.id || index}
        className={`grid items-center gap-2 px-3 py-3 border-b text-sm ${ex.is_active === false ? 'bg-muted/30 opacity-60' : 'bg-white'}`}
        style={{ gridTemplateColumns: '90px 1.4fr 1.6fr 1.4fr 1.2fr 1fr 90px' }}
        data-testid={`training-example-${index}`}
      >
        <div className="flex items-center gap-1.5">
          {dirIn ? <ArrowDownRight className="w-3.5 h-3.5 text-emerald-600" /> : <ArrowUpRight className="w-3.5 h-3.5 text-red-600" />}
          <span className="text-xs">{dirIn ? 'In' : 'Out'}</span>
        </div>
        <div className="truncate" title={ex.counterparty}>{ex.counterparty || <span className="text-muted-foreground italic">-</span>}</div>
        <div className="truncate text-muted-foreground" title={ex.description}>{ex.description || '-'}</div>
        <div className="text-xs text-foreground/80">{minMaxLabel}</div>
        <div>
          <Badge className={`text-[10px] ${dirIn ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'} hover:bg-opacity-100`}>
            {(ex.classification || '').replace(/_/g, ' ')}
          </Badge>
        </div>
        <div className="text-xs">
          {ex.gl_code ? (
            <span><span className="font-mono text-[10px] text-muted-foreground mr-1">{ex.gl_code}</span>{ex.gl_name || ''}</span>
          ) : <span className="text-muted-foreground">-</span>}
        </div>
        <div className="flex items-center justify-end gap-0.5">
          {ex.times_matched > 0 && (
            <Badge variant="outline" className="text-[10px] mr-1" title="Times this rule matched a transaction">{ex.times_matched}</Badge>
          )}
          <Button
            size="icon"
            variant="ghost"
            className={`h-8 w-8 ${ex.is_active === false ? 'text-muted-foreground' : 'text-emerald-700'}`}
            onClick={() => onToggleActive?.(ex.id, ex.is_active)}
            title={ex.is_active === false ? 'Activate rule' : 'Deactivate rule'}
            data-testid={`toggle-training-${index}`}
          >
            {ex.is_active === false ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 text-red-600 hover:bg-red-50"
            onClick={() => onDelete?.(ex.id)}
            title="Delete rule"
            data-testid={`delete-training-${index}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  const empty = useMemo(
    () => !loading && existingExamples.length === 0 && drafts.length === 0,
    [loading, existingExamples.length, drafts.length],
  );

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="train-ai-table">
      {headerRow}

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
          <span className="ml-2 text-sm text-muted-foreground">Loading training rules…</span>
        </div>
      )}

      {!loading && existingExamples.map((ex, i) => renderExistingRow(ex, i))}

      <AnimatePresence>
        {drafts.map(renderDraftRow)}
      </AnimatePresence>

      {empty && (
        <div className="text-center py-10 px-4">
          <Brain className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium">No training rules yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add a rule below to teach the AI how to classify your transactions.</p>
        </div>
      )}

      <div className="flex items-center justify-between px-3 py-2.5 bg-muted/20 border-t">
        <Button
          size="sm"
          variant="outline"
          onClick={addDraft}
          className="h-8 gap-1.5"
          data-testid="train-ai-add-row"
        >
          <Plus className="w-3.5 h-3.5" />
          Add rule
        </Button>
        {drafts.length > 1 && (
          <Button
            size="sm"
            onClick={saveAll}
            disabled={batchSaving}
            className="h-8 gap-1.5 bg-purple-600 hover:bg-purple-700"
            data-testid="train-ai-save-all"
          >
            {batchSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save all ({drafts.length})
          </Button>
        )}
      </div>
    </div>
  );
};

export default TrainAITable;
export { TrainAITable };
